import React, { useState, useEffect, useMemo } from 'react';
import PlusCircle from 'lucide-react/dist/esm/icons/plus-circle.js';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2.js';
import GripVertical from 'lucide-react/dist/esm/icons/grip-vertical.js';
import Images from 'lucide-react/dist/esm/icons/images.js';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import { useCharityTheme } from '../../../contexts/CharityThemeContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import ImagePickerModal from '../../../components/ImagePickerModal';
import {
  AboutBannerLogoEnabled,
  AboutBannerLogoUrl,
  AboutBannerSlideList,
  AboutTitleEnabled,
  AboutTitleTitle,
} from '../../../content/keys';

// Content keys for editable hero
const AboutHeroTitle = 'AboutHeroTitle';
const AboutHeroSubtitle = 'AboutHeroSubtitle';

export default function AboutHeroSection({ section }) {
  const { c, updateContent } = useContent();
  const { editMode } = useEditMode();
  const { charityName, branding } = useCharityTheme();
  const { t: tr } = useTranslations();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);

  // Banner slides from content system
  const bannerSlides = useMemo(() => {
    const raw = c(AboutBannerSlideList, '');
    return raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
  }, [c]);

  // Auto-rotate banner slides
  useEffect(() => {
    if (bannerSlides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % bannerSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bannerSlides.length]);

  // Content flags
  const showBannerLogo = c(AboutBannerLogoEnabled, '') === 'True';
  const bannerLogoUrl = c(AboutBannerLogoUrl, '');
  const showTitle = c(AboutTitleEnabled, '') === 'True';
  const titleText = c(AboutTitleTitle, '');

  // Reset logo failure flag if the configured logo URL changes
  useEffect(() => {
    setLogoLoadFailed(false);
  }, [bannerLogoUrl]);

  // Hero title/subtitle — use content keys, fall back to translated defaults
  const heroTitle = c(AboutHeroTitle, '') || tr('about.title') || 'About Us';
  const heroSubtitleTemplate = tr('about.heroSubtitle') || "Together with {{charity}}, we're making a difference in our community \u2014 one ticket at a time.";
  const resolvedCharityName = charityName || branding?.charityName || '';
  const heroSubtitle = c(AboutHeroSubtitle, '') || heroSubtitleTemplate.replace('{{charity}}', resolvedCharityName);

  // Helper: persist slide list back to draft/content
  const saveSlides = (slides) => {
    updateContent(AboutBannerSlideList, slides.join(','));
  };

  const handleAddSlide = (url) => {
    const next = [...bannerSlides.filter(Boolean), url];
    saveSlides(next);
    setPickerOpen(false);
  };

  const handleRemoveSlide = (idx) => {
    const next = bannerSlides.filter((_, i) => i !== idx);
    saveSlides(next);
    if (currentSlide >= next.length) setCurrentSlide(Math.max(0, next.length - 1));
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const next = [...bannerSlides];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    saveSlides(next);
    setDragIdx(idx);
  };
  const handleDragEnd = () => setDragIdx(null);

  // Hide the banner entirely in view mode when no slides are configured
  if (!editMode && bannerSlides.length === 0) return null;

  return (
    <div
      className="relative py-10 xs:py-14 sm:py-24 md:py-32 px-3 sm:px-6 lg:px-8 overflow-hidden rounded-2xl mb-4 sm:mb-12 md:mb-16 animate-fade-in"
      style={bannerSlides.length > 0 ? {
        backgroundImage: `url(${bannerSlides[currentSlide]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 0.6s ease-in-out',
      } : {
        background: 'linear-gradient(135deg, var(--charity-primary, #1e40af), var(--charity-primary-dark, #1e3a8a))',
      }}
    >
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background: bannerSlides.length > 0
            ? 'linear-gradient(135deg, var(--charity-primary-darkest, rgba(0,0,0,0.55)) 0%, rgba(0,0,0,0.35) 100%)'
            : undefined,
        }}
      />
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-20 w-64 h-64 bg-white rounded-full animate-float-slow" />
        <div className="absolute top-40 right-32 w-48 h-48 bg-white rounded-full animate-float-medium" />
        <div className="absolute bottom-32 left-1/3 w-56 h-56 bg-white rounded-full animate-float-fast" />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        {showBannerLogo && bannerLogoUrl && !logoLoadFailed && (
          <img
            src={bannerLogoUrl}
            alt=""
            className="h-10 sm:h-16 w-auto mx-auto mb-4 sm:mb-8 drop-shadow-lg"
            onError={() => setLogoLoadFailed(true)}
          />
        )}
        <h1 className="text-2xl xs:text-3xl sm:text-5xl md:text-6xl font-bold text-white mb-3 sm:mb-6 drop-shadow-lg leading-tight">
          <InlineTranslationEdit translationKey={AboutHeroTitle} value={heroTitle} />
        </h1>
        <p className="text-xs xs:text-sm sm:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
          <InlineTranslationEdit translationKey={AboutHeroSubtitle} value={heroSubtitle} multiline />
        </p>
        {showTitle && titleText && (
          <h2 className="text-base sm:text-2xl text-white/80 mt-2 sm:mt-4">
            <InlineTranslationEdit translationKey={AboutTitleTitle} value={titleText} />
          </h2>
        )}
      </div>

      {/* ── Admin: banner slide manager ── */}
      {editMode && (
        <div className="absolute bottom-4 left-4 right-4 z-20">
          <div className="bg-black/70 backdrop-blur-sm rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-1.5 text-white text-sm font-semibold">
                <Images className="w-4 h-4" />
                Banner Slides
                <span className="ml-1 text-white/60 font-normal">({bannerSlides.length})</span>
              </span>
              <button
                onClick={() => setPickerOpen(true)}
                className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
              >
                <PlusCircle className="w-3.5 h-3.5" /> Add slide
              </button>
            </div>

            {bannerSlides.length === 0 ? (
              <p className="text-white/50 text-xs text-center py-2">
                No slides — the theme colour gradient will show instead. Click "Add slide" to add images.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {bannerSlides.map((url, idx) => (
                  <div
                    key={url + idx}
                    draggable
                    onDragStart={() => handleDragStart(idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDragEnd={handleDragEnd}
                    onClick={() => setCurrentSlide(idx)}
                    className={`relative group w-16 h-12 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      currentSlide === idx ? 'border-white' : 'border-transparent hover:border-white/50'
                    } ${dragIdx === idx ? 'opacity-50' : ''}`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                      <GripVertical className="w-3 h-3 text-white" />
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveSlide(idx); }}
                        className="p-0.5 rounded bg-red-500/80 hover:bg-red-500 text-white"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <span className="absolute bottom-0.5 right-1 text-white text-[10px] font-bold drop-shadow">{idx + 1}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {pickerOpen && (
        <ImagePickerModal
          onSelect={handleAddSlide}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
